import {
	getGetEventQueryKey,
	getListEventsQueryKey,
	useDeleteEvent,
} from '@cocoonspace/sdk-js/domains/events/events'
import type { Event } from '@cocoonspace/sdk-js/types/event'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { useQueryClient } from '@tanstack/react-query'
import { Trans, useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import type { PropsWithChildren } from 'react'
import { toast } from 'sonner'

import { AlertDialogOrDrawer } from '~/components/ui/alert-dialog-or-drawer'
import { useTracking } from '~/providers/tracking-provider'

export const EventCancellationConfirmDialog = ({
	event,
	children,
	isOption = false,
}: PropsWithChildren<{
	event: Event
	isOption?: boolean
}>) => {
	const router = useRouter()
	const queryClient = useQueryClient()
	const { t } = useTranslation()
	const { track } = useTracking()

	const deleteEventMutation = useDeleteEvent(
		isOption ? { request: { params: { option: true } } } : undefined,
	)

	const wording = isOption
		? {
				title: 'cwEvents:cancelPopover.titleOption',
				body: 'cwEvents:cancelPopover.bodyOption',
				successMsg: 'cwEvents:cancelPopover.successMsgOption',
				errorMsg: 'cwEvents:cancelPopover.errorMsgOption',
			}
		: {
				title: 'cwEvents:cancelPopover.title',
				body: 'cwEvents:cancelPopover.body',
				successMsg: 'cwEvents:cancelPopover.successMsg',
				errorMsg: 'cwEvents:cancelPopover.errorMsg',
			}

	const onConfirm = () =>
		deleteEventMutation
			.mutateAsync({ eventId: event.id })
			.then(() => {
				queryClient.invalidateQueries({
					queryKey: getGetEventQueryKey(event.id),
				})
				queryClient.invalidateQueries({ queryKey: getListEventsQueryKey() })

				toast.success(t(wording.successMsg))

				// TODO: to finish
				track.cancelEvent({
					event,
					route: router.route,
				})
			})
			.catch((err) => {
				toast.error(t(wording.errorMsg))
			})

	return (
		<AlertDialogOrDrawer
			title={t(wording.title, {
				date: dateTimeUtils(event.start_local).format('D MMMM'),
				start: dateTimeUtils(event.start_local).customFormat('hours'),
				end: dateTimeUtils(event.end_local).customFormat('hours'),
			})}
			confirmLabel={t('actions:confirm.btnLabel')}
			cancelLabel={t('actions:cancel.btnLabel')}
			trigger={children}
			onConfirm={onConfirm}
		>
			<div className='flex flex-col justify-start gap-4'>
				<div>
					<Trans
						i18nKey={wording.body}
						values={{
							day: dateTimeUtils(event.refundable_until).format('D MMMM'),
							time: dateTimeUtils(event.refundable_until).customFormat('hours'),
						}}
					/>
				</div>

				{!isOption && !event.extras_updatable && !!event.extras?.length && (
					<div>
						<Trans i18nKey='cwEvents:cancelPopover.extras' />
					</div>
				)}
			</div>
		</AlertDialogOrDrawer>
	)
}
